"use client";

import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { getPostBySlug, PostData } from '@/lib/markdown';

interface LegalModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'terms' | 'privacy' | 'cookies';
}

const LegalModal = ({ isOpen, onClose, type }: LegalModalProps) => {
  const [content, setContent] = useState<PostData | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      getPostBySlug('legal', type).then(data => {
        setContent(data);
        setLoading(false);
      });
    }
  }, [isOpen, type]);

  const getTitle = () => {
    if (content?.title) return content.title;
    switch (type) {
      case 'terms': return "Terms of Use";
      case 'privacy': return "Privacy Policy";
      case 'cookies': return "Cookie Policy";
      default: return "Legal Information";
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl w-[95vw] max-h-[85vh] bg-slate-950/90 border-white/10 backdrop-blur-2xl text-slate-200 p-0 overflow-hidden shadow-2xl">
        <DialogHeader className="p-6 border-b border-white/5">
          <DialogTitle className="text-2xl font-bold text-white">
            {getTitle()}
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="p-6 md:p-10 h-[calc(85vh-80px)]">
          <div className="pb-10">
            {loading ? (
              <div className="flex justify-center py-10">
                <div className="w-8 h-8 border-4 border-fuchsia-500/20 border-t-fuchsia-500 rounded-full animate-spin" />
              </div>
            ) : content ? (
              <div className="prose prose-invert prose-fuchsia max-w-none 
                prose-headings:text-fuchsia-400 prose-headings:font-bold prose-headings:mt-8 prose-headings:mb-4
                prose-p:text-slate-300 prose-p:leading-relaxed prose-p:mb-4
                prose-li:text-slate-300 prose-li:mb-2
                prose-strong:text-white">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {content.content}
                </ReactMarkdown>
              </div>
            ) : (
              <p className="text-slate-500 italic">Content not found...</p>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default LegalModal;